import { useState, useEffect } from "react";
import { 
  BarChart3,
  LineChart, 
  BarChart, 
  PieChart, 
  Mail, 
  MailOpen, 
  MousePointer, 
  MessageSquare, 
  AlertTriangle,
  RefreshCw,
  Calendar,
  ChevronRight
} from "lucide-react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { CampaignMetricsCard } from "./CampaignMetricsCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  getCampaignAnalytics,
  CampaignAnalytics,
  CampaignMetrics,
  PeriodMetric
} from "@/services/communication/campaignAnalyticsService";

interface CampaignAnalyticsDashboardProps {
  initialPeriod?: "week" | "month" | "quarter" | "year";
}

export function CampaignAnalyticsDashboard({
  initialPeriod = "month"
}: CampaignAnalyticsDashboardProps) {
  const [period, setPeriod] = useState<"week" | "month" | "quarter" | "year">(initialPeriod);
  
  // Query for analytics data
  const { 
    data: analytics,
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ["campaignAnalytics", period],
    queryFn: () => getCampaignAnalytics({ period }),
  });
  
  // Handler for period change
  const handlePeriodChange = (value: string) => {
    setPeriod(value as "week" | "month" | "quarter" | "year");
  };
  
  if (isLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }
  
  if (isError || !analytics) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <AlertTriangle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">Failed to load analytics data</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  
  // Format number to percentage
  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Campaign Analytics</h2>
        
        <div className="flex items-center gap-2">
          <Select 
            value={period} 
            onValueChange={handlePeriodChange}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last Week</SelectItem>
              <SelectItem value="month">Last Month</SelectItem>
              <SelectItem value="quarter">Last Quarter</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex flex-col space-y-0.5">
              <CardTitle className="text-sm font-medium">
                Campaigns
              </CardTitle>
              <CardDescription>Total sent</CardDescription>
            </div>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics.summary.totalCampaigns}
            </div>
            <p className="text-xs text-muted-foreground">
              {analytics.summary.totalRecipients.toLocaleString()} total recipients
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex flex-col space-y-0.5">
              <CardTitle className="text-sm font-medium">
                Average Open Rate
              </CardTitle>
              <CardDescription>Across campaigns</CardDescription>
            </div>
            <MailOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercent(analytics.summary.averageOpenRate)}
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-muted">
              <div 
                className="h-1.5 rounded-full bg-primary" 
                style={{ width: `${analytics.summary.averageOpenRate}%` }} 
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex flex-col space-y-0.5">
              <CardTitle className="text-sm font-medium">
                Average Click Rate
              </CardTitle>
              <CardDescription>Across campaigns</CardDescription>
            </div>
            <MousePointer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercent(analytics.summary.averageClickRate)}
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-muted">
              <div 
                className="h-1.5 rounded-full bg-primary" 
                style={{ width: `${analytics.summary.averageClickRate}%` }} 
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex flex-col space-y-0.5">
              <CardTitle className="text-sm font-medium">
                Response Rate
              </CardTitle>
              <CardDescription>Across campaigns</CardDescription>
            </div>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercent(analytics.summary.averageResponseRate)}
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-muted">
              <div 
                className="h-1.5 rounded-full bg-primary" 
                style={{ width: `${analytics.summary.averageResponseRate}%` }} 
              />
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Campaign Performance Over Time</CardTitle>
            <CardDescription>Open and click rates by campaign period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {/* In a real implementation, this would be a chart component */}
              <div className="flex h-full w-full flex-col justify-end">
                <div className="flex h-[240px] items-end gap-2">
                  {analytics.summary.campaignsOverTime.map((periodData, index) => (
                    <div key={index} className="group relative flex w-full flex-1 flex-col items-center">
                      <div className="relative flex h-full w-full flex-col justify-end gap-1">
                        <div 
                          className="w-full bg-primary/10 hover:bg-primary/20 transition-colors"
                          style={{ height: `${periodData.openRate}%` }}
                        >
                          <div className="absolute inset-x-0 -top-7 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Badge variant="outline" className="bg-background">
                              {formatPercent(periodData.openRate)} Opens
                            </Badge>
                          </div>
                        </div>
                        <div 
                          className="w-full bg-primary hover:bg-primary/90 transition-colors"
                          style={{ height: `${periodData.clickRate}%` }}
                        >
                          <div className="absolute inset-x-0 bottom-12 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Badge variant="outline" className="bg-background">
                              {formatPercent(periodData.clickRate)} Clicks
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="pt-2 text-xs">
                        {new Date(periodData.period).toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid gap-4 md:grid-cols-3">
        {/* Top Performing Campaigns */}
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle>Top Performing Campaigns</CardTitle>
            <CardDescription>Highest engagement rates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.topPerforming.map((campaign) => (
                <div key={campaign.id} className="flex items-center space-x-4">
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium leading-none">{campaign.name}</p>
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Calendar className="mr-1 h-3 w-3" />
                      <span>{new Date(campaign.period + "-01").toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-right">
                      <p className="text-sm font-medium">{formatPercent(campaign.openRate)}</p>
                      <p className="text-xs text-muted-foreground">Opens</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter>
            <Button variant="ghost" className="w-full" size="sm">
              View all campaigns
            </Button>
          </CardFooter>
        </Card>
        
        {/* Campaign Type Performance */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Campaign Type Performance</CardTitle>
            <CardDescription>Open and click rates by campaign type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.campaignTypes.map((type, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <div className="flex items-center">
                        <span className="text-sm font-medium">{type.name}</span>
                        <Badge className="ml-2" variant="outline">
                          {type.count}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <MailOpen className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{formatPercent(type.openRate)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MousePointer className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{formatPercent(type.clickRate)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span>Open Rate</span>
                        <span>{formatPercent(type.openRate)}</span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-muted">
                        <div 
                          className="h-1.5 rounded-full bg-primary" 
                          style={{ width: `${type.openRate}%` }} 
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span>Click Rate</span>
                        <span>{formatPercent(type.clickRate)}</span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-muted">
                        <div 
                          className="h-1.5 rounded-full bg-primary" 
                          style={{ width: `${type.clickRate}%` }} 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Recent Campaigns */}
        <Card className="md:col-span-2 lg:col-span-3">
          <CardHeader>
            <CardTitle>Recent Campaigns</CardTitle>
            <CardDescription>Performance metrics for your latest campaigns</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {analytics.campaigns.slice(0, 3).map((campaign) => (
                <CampaignMetricsCard key={campaign.id} metrics={campaign} />
              ))}
            </div>
          </CardContent>
          <CardFooter>
            <Button variant="outline" className="w-full">
              View All Campaigns
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}